import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowsClockwiseBold,
    PiCheckCircleFill,
    PiClockCounterClockwise,
    PiCopyBold,
    PiLinkBreakBold,
    PiLinkSimpleBold,
    PiPencilSimpleBold,
    PiUserCircleDashedBold,
    PiUserMinusBold,
    PiUsersThreeBold,
    PiWarningBold,
    PiXCircleFill,
} from 'react-icons/pi';
import { Button } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import UsersAdminTabs from '../features/users/UsersAdminTabs';
import RichListShell, { BadgePill, PaginationMeta, toneFromFilament } from '../components/RichListShell';
import { DeactivationButton } from '@v2/features/users/UserDeactivation';

type User = {
    id: number;
    name: string;
    email: string;
    phone: string | null;
    department: string | null;
    job_title: string | null;
    avatar_url: string | null;
    roles: Array<{ name: string; label?: string; color: string }>;
    is_active?: boolean;
    deactivated_at?: string | null;
    deactivation_reason?: string | null;
    ploomes_id?: number | null;
    ploomes_suspended?: boolean;
    ploomes_suspended_at?: string | null;
    last_login_at?: string | null;
    last_login_label?: string | null;
};

type Counts = { active: number; inactive: number; no_roles: number; no_ploomes: number; never_logged: number; ploomes_suspended: number };

type ApiResponse = {
    data: User[];
    meta: PaginationMeta & { counts?: Counts; departments?: string[]; roles?: { value: string; label: string }[] };
};

type Status = 'active' | 'inactive' | 'all';
type Flag = '' | 'no_roles' | 'no_ploomes' | 'never_logged' | 'ploomes_suspended';

const FALLBACK = '/img/favicon.png';

function relative(iso?: string | null): string {
    if (!iso) return 'sem registro';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days <= 0) return 'hoje';
    if (days === 1) return 'ontem';
    if (days < 30) return `há ${days} dias`;
    const months = Math.floor(days / 30);
    return months < 12 ? `há ${months} ${months === 1 ? 'mês' : 'meses'}` : `há ${Math.floor(months / 12)} ano(s)`;
}

function Kpi({
    label,
    value,
    hint,
    icon,
    tone,
    active,
    onClick,
}: {
    label: string;
    value?: number;
    hint: string;
    icon: ReactNode;
    tone: string;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={cn(
                'flex items-start gap-3 rounded-xl border bg-white p-3 text-left transition hover:shadow-sm dark:bg-gray-800',
                active ? 'border-primary ring-1 ring-primary/30' : 'border-gray-200 dark:border-gray-700',
            )}
        >
            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', tone)}>{icon}</span>
            <span className="min-w-0">
                <span className="block text-xl font-bold tabular-nums leading-none text-gray-900 dark:text-gray-100">{value ?? '—'}</span>
                <span className="mt-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">{label}</span>
                <span className="block text-[11px] text-gray-400">{hint}</span>
            </span>
        </button>
    );
}

export default function UserListPage() {
    const qc = useQueryClient();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [status, setStatus] = useState<Status>('active');
    const [flag, setFlag] = useState<Flag>('');
    const [role, setRole] = useState('');
    const [department, setDepartment] = useState('');
    const [sort, setSort] = useState<'name' | 'last_login'>('name');

    useEffect(() => {
        setPage(1);
    }, [search, perPage, status, flag, role, department, sort]);

    const params = useMemo(() => {
        const p: Record<string, string> = { page: String(page), per_page: String(perPage), status, sort };
        if (search) p.search = search;
        if (flag) p.flag = flag;
        if (role) p.role = role;
        if (department) p.department = department;
        return p;
    }, [search, page, perPage, status, flag, role, department, sort]);

    const { data, isLoading } = useQuery({
        queryKey: ['users', params],
        queryFn: async () => (await api.get<ApiResponse>('/users', { params })).data,
        placeholderData: (prev) => prev,
    });

    const syncMutation = useMutation({
        mutationFn: async () => (await api.post<{ message: string }>('/users/sync-ploomes')).data,
        onSuccess: (res) => {
            toast.success(res.message);
            qc.invalidateQueries({ queryKey: ['users'] });
        },
        onError: () => toast.error('Falha ao sincronizar usuários com o Ploomes.'),
    });

    const copy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('E-mail copiado.');
        } catch {
            /* sem área de transferência */
        }
    };

    const rows = data?.data ?? [];
    const counts = data?.meta.counts;
    const filtersActive = [flag, role, department].filter(Boolean).length + (status !== 'active' ? 1 : 0);
    const pickKpi = (nextStatus: Status, nextFlag: Flag) => {
        const same = status === nextStatus && flag === nextFlag;
        setStatus(same ? 'active' : nextStatus);
        setFlag(same ? '' : nextFlag);
    };
    const clear = () => {
        setStatus('active');
        setFlag('');
        setRole('');
        setDepartment('');
    };

    return (
        <>
            <UsersAdminTabs />


            <RichListShell<User>
                title="Usuários"
                breadcrumb={[{ name: 'Administração' }, { name: 'Usuários' }]}
                intro={
            <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-6">
                <Kpi label="Ativos" value={counts?.active} hint="Podem entrar no Hub" icon={<PiCheckCircleFill className="size-5" />} tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300" active={status === 'active' && flag === ''} onClick={() => pickKpi('active', '')} />
                <Kpi label="Desativados" value={counts?.inactive} hint="Desligados — histórico mantido" icon={<PiUserMinusBold className="size-5" />} tone="bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300" active={status === 'inactive'} onClick={() => pickKpi('inactive', '')} />
                <Kpi label="Sem perfil" value={counts?.no_roles} hint="Entram mas não veem quase nada" icon={<PiUserCircleDashedBold className="size-5" />} tone="bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300" active={flag === 'no_roles'} onClick={() => pickKpi('active', 'no_roles')} />
                <Kpi label="Suspensos no Ploomes" value={counts?.ploomes_suspended} hint="Ativos aqui — confira se foi desligamento" icon={<PiWarningBold className="size-5" />} tone="bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300" active={flag === 'ploomes_suspended'} onClick={() => pickKpi('active', 'ploomes_suspended')} />
                <Kpi label="Vendedor sem Ploomes" value={counts?.no_ploomes} hint="Sem vínculo: vendas e comissão não aparecem" icon={<PiLinkBreakBold className="size-5" />} tone="bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300" active={flag === 'no_ploomes'} onClick={() => pickKpi('active', 'no_ploomes')} />
                <Kpi label="Sem acesso registrado" value={counts?.never_logged} hint="O Hub registra o acesso a partir de agora" icon={<PiUsersThreeBold className="size-5" />} tone="bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300" active={flag === 'never_logged'} onClick={() => pickKpi('active', 'never_logged')} />
            </div>
                }
                search={search}
                onSearch={setSearch}
                searchPlaceholder="Buscar por nome, e-mail, cargo ou departamento"
                isLoading={isLoading}
                rows={rows}
                rowKey={(r) => r.id}
                detailPath={(r) => `/users/${r.id}`}
                pagination={data?.meta}
                onPageChange={setPage}
                onPerPageChange={setPerPage}
                activeFiltersCount={filtersActive}
                emptyMessage="Nenhum usuário com esses filtros."
                filtersPanel={
                    <div className="flex flex-wrap items-end gap-3">
                        <label className="text-xs">
                            <span className="mb-1 block font-semibold text-gray-600 dark:text-gray-300">Situação</span>
                            <select value={status} onChange={(e) => setStatus(e.target.value as Status)} className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900">
                                <option value="active">Ativos</option>
                                <option value="inactive">Desativados</option>
                                <option value="all">Todos</option>
                            </select>
                        </label>
                        <label className="text-xs">
                            <span className="mb-1 block font-semibold text-gray-600 dark:text-gray-300">Perfil</span>
                            <select value={role} onChange={(e) => setRole(e.target.value)} className="h-9 min-w-[12rem] rounded-lg border border-gray-200 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900">
                                <option value="">Todos os perfis</option>
                                {(data?.meta.roles ?? []).map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-xs">
                            <span className="mb-1 block font-semibold text-gray-600 dark:text-gray-300">Departamento</span>
                            <select value={department} onChange={(e) => setDepartment(e.target.value)} className="h-9 min-w-[10rem] rounded-lg border border-gray-200 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900">
                                <option value="">Todos</option>
                                {(data?.meta.departments ?? []).map((d) => (
                                    <option key={d} value={d}>
                                        {d}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-xs">
                            <span className="mb-1 block font-semibold text-gray-600 dark:text-gray-300">Ordenar por</span>
                            <select value={sort} onChange={(e) => setSort(e.target.value as 'name' | 'last_login')} className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900">
                                <option value="name">Nome</option>
                                <option value="last_login">Último acesso</option>
                            </select>
                        </label>
                        {filtersActive > 0 && (
                            <button type="button" onClick={clear} className="h-9 text-xs font-semibold text-primary hover:underline">
                                Limpar filtros
                            </button>
                        )}
                    </div>
                }
                headerActions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
                            <PiArrowsClockwiseBold className={`me-2 size-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                            {syncMutation.isPending ? 'Sincronizando…' : 'Sincronizar Ploomes'}
                        </Button>
                        <Link to="/users/new">
                            <Button as="span" size="sm">
                                + Novo usuário
                            </Button>
                        </Link>
                    </div>
                }
                columns={[
                    {
                        key: 'name',
                        label: 'Usuário',
                        render: (r) => (
                            <div className={cn('flex min-w-0 items-center gap-3', r.is_active === false && 'opacity-60')}>
                                <img
                                    src={r.avatar_url || FALLBACK}
                                    alt=""
                                    className={cn('size-9 shrink-0 rounded-full border border-gray-100 bg-gray-50 object-cover', r.is_active === false && 'grayscale')}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = FALLBACK;
                                    }}
                                />
                                <span className="min-w-0">
                                    <span className="block truncate font-semibold text-gray-900 dark:text-gray-100">{r.name}</span>
                                    <span className="flex items-center gap-1 text-[11px] text-gray-500">
                                        <span className="truncate">{r.email}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                void copy(r.email);
                                            }}
                                            title="Copiar e-mail"
                                            aria-label="Copiar e-mail"
                                            className="shrink-0 rounded p-0.5 text-gray-300 hover:text-gray-600"
                                        >
                                            <PiCopyBold className="size-3" />
                                        </button>
                                    </span>
                                </span>
                            </div>
                        ),
                    },
                    {
                        key: 'status',
                        label: 'Status',
                        width: '11rem',
                        render: (r) =>
                            r.is_active === false ? (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500" title={[r.deactivated_at && `Desativado em ${r.deactivated_at}`, r.deactivation_reason].filter(Boolean).join(' · ')}>
                                    <PiXCircleFill className="size-4 text-gray-400" /> Desativado
                                </span>
                            ) : (
                                <span className="inline-flex flex-col items-start gap-1">
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                                        <PiCheckCircleFill className="size-4 text-emerald-500" /> Ativo
                                    </span>
                                    {r.ploomes_suspended && (
                                        <span
                                            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-500/15 dark:text-orange-300"
                                            title={`Suspenso no Ploomes${r.ploomes_suspended_at ? ` desde ${r.ploomes_suspended_at}` : ''}. Se foi desligamento, desative aqui também.`}
                                        >
                                            <PiWarningBold className="size-3" /> Suspenso no Ploomes
                                        </span>
                                    )}
                                </span>
                            ),
                    },
                    {
                        key: 'roles',
                        label: 'Perfis',
                        render: (r) =>
                            r.roles.length === 0 ? (
                                <span className="text-xs font-semibold text-amber-600">Sem perfil</span>
                            ) : (
                                <div className="flex flex-wrap gap-1">
                                    {r.roles.map((role) => (
                                        <BadgePill key={role.name} tone={toneFromFilament(role.color)}>
                                            {role.label ?? role.name}
                                        </BadgePill>
                                    ))}
                                </div>
                            ),
                    },
                    {
                        key: 'job_title',
                        label: 'Cargo · Departamento',
                        render: (r) =>
                            r.job_title || r.department ? (
                                <span className="text-xs">
                                    <span className="block text-gray-800 dark:text-gray-100">{r.job_title ?? '—'}</span>
                                    <span className="block text-gray-400">{r.department ?? ''}</span>
                                </span>
                            ) : (
                                <span className="text-xs text-gray-300">—</span>
                            ),
                    },
                    {
                        key: 'ploomes',
                        label: 'Ploomes',
                        width: '6.5rem',
                        render: (r) =>
                            r.ploomes_id ? (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300" title={`ID no Ploomes: ${r.ploomes_id}`}>
                                    <PiLinkSimpleBold className="size-3.5 text-emerald-500" /> Vinculado
                                </span>
                            ) : (
                                <span className="text-xs text-gray-300" title="Sem vínculo com usuário do Ploomes">
                                    —
                                </span>
                            ),
                    },
                    {
                        key: 'last_login',
                        label: 'Último acesso',
                        width: '8rem',
                        render: (r) => (
                            <span className={cn('text-xs', r.last_login_at ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300')} title={r.last_login_label ?? undefined}>
                                {relative(r.last_login_at)}
                            </span>
                        ),
                    },
                    {
                        key: 'actions',
                        label: '',
                        width: '7rem',
                        render: (r) => (
                            <div className="flex items-center justify-end gap-1">
                                <Link
                                    to={`/users/${r.id}/activity-log`}
                                    onClick={(e) => e.stopPropagation()}
                                    title="Log de atividade"
                                    aria-label="Log de atividade"
                                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-indigo-600 dark:hover:bg-gray-700"
                                >
                                    <PiClockCounterClockwise className="size-4" />
                                </Link>
                                <Link
                                    to={`/users/${r.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    title="Editar"
                                    aria-label="Editar"
                                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-700"
                                >
                                    <PiPencilSimpleBold className="size-4" />
                                </Link>
                                <DeactivationButton user={r} compact />
                            </div>
                        ),
                    },
                ]}
            />
        </>
    );
}
